import {
  Col,
  Divider,
  Dropdown,
  DropdownProps,
  Flex,
  Menu,
  MenuProps,
  Row,
  theme,
} from 'antd'
import { HTMLProps, Key, ReactNode, useEffect, useState } from 'react'
import { DownOutlined } from '@ant-design/icons'
import {
  CustomMenuItemType,
  CustomSubMenuType,
  NO_NAVIGATE_KEY,
} from './custom-menu-items-type'
import searchMenuTree from './helper/search-menu-tree'
import {
  isGroupMenuType,
  isSubMenuType,
} from './helper/custom-menu-item-type-guard'
import MenuSearch from '../MenuSearch'

interface IProps {
  item: CustomMenuItemType<'dynamic'>
  onSelectMenuSearchItem: (_key: string) => void
  containerProps?: HTMLProps<HTMLDivElement>
  menuProps?: MenuProps
  dropdownProps?: DropdownProps
  labelRender?: ReactNode
}

const DynamicMenuRender = ({
  item,
  onSelectMenuSearchItem,
  containerProps,
  menuProps,
  dropdownProps,
  labelRender,
}: IProps) => {
  const themeToken = theme.useToken().token
  const [selectKey, setSelectKey] = useState<Key | undefined>()

  const menuSearch = (
    <MenuSearch menuItems={item.children} onSelect={onSelectMenuSearchItem} />
  )

  // Vị trí router hiện tại
  const menuSelectKey = menuProps?.selectedKeys?.[0]
  // Menu đang chọn hiện tại
  const selectOption = item.children.find((x) => x.key === selectKey)
  // Danh sách children render ra
  const showChildren = selectOption?.children
  // Option chứa vị trí router hiện tại
  const targetOptKey =
    menuSelectKey && searchMenuTree(item.children, menuSelectKey)

  // Auto select item đầu tiên khi chuyển module
  const selectNestedFirstKey = (child: CustomSubMenuType): string => {
    const childKey = child.key?.toString() ?? ''
    // console.log(child)

    if (
      // Nếu item hiện tại không navigate được
      childKey.includes(NO_NAVIGATE_KEY) &&
      // và nếu có children
      (isSubMenuType(child) || isGroupMenuType(child))
    ) {
      const firstChildren = child.children?.[0]
      if (firstChildren)
        return selectNestedFirstKey(firstChildren as CustomSubMenuType)
    }

    return child.key?.toString()
  }

  const dropdownItems: MenuProps['items'] = item.children.map((child) => ({
    ...child,
    onClick: (e) => {
      setSelectKey(child.key)
      // Thực thi onClick
      // Nếu không có children thì click parent
      if (child.children?.length <= 0) {
        menuProps?.onClick?.(e)
        // Hoặc click child đầu tiên
      } else if (child.children?.[0]?.key) {
        menuProps?.onClick?.({
          ...e,
          key:
            selectNestedFirstKey(child.children[0] as CustomSubMenuType) ||
            e.key,
        })
      }
    },
    style:
      // Khi vị trí hiện tại khác menu đang chọn thì highlight để biết đang ở mục nào
      targetOptKey === child.key && targetOptKey !== selectKey
        ? { fontWeight: 'bolder' }
        : undefined,
    type: 'item',
    children: undefined,
  }))

  dropdownItems.unshift({
    key: 'menuSearch',
    label: menuSearch,
    type: 'item',
    style: { cursor: 'default', backgroundColor: 'inherit' },
  })

  const dynamicSelect = selectOption ? (
    <Dropdown
      menu={{
        items: dropdownItems,
        selectedKeys: [selectOption.key],
      }}
      trigger={['click']}
      {...dropdownProps}
    >
      <div
        style={{ padding: '0 16px 0 24px', marginBlock: 8, cursor: 'pointer' }}
      >
        {labelRender || (
          <Row gutter={8} align="middle" wrap={false}>
            {selectOption.icon && (
              <Col>
                <Flex
                  style={{
                    color: themeToken.colorPrimary,
                    height: '100%',
                  }}
                  align="center"
                >
                  {selectOption.icon}
                </Flex>
              </Col>
            )}
            <Col flex={1}>
              {typeof selectOption.label === 'string' ? (
                <span
                  style={{
                    color: themeToken.colorPrimary,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    fontWeight: themeToken.fontWeightStrong,
                  }}
                >
                  {selectOption.label}
                </span>
              ) : (
                selectOption.label
              )}
            </Col>
            <Col>
              <DownOutlined />
            </Col>
          </Row>
        )}
      </div>
    </Dropdown>
  ) : null

  // Sync Vị trí router hiện tại và Menu đang chọn hiện tại
  useEffect(() => {
    if (!targetOptKey) {
      setSelectKey(undefined)
      return
    }

    if (!selectKey || targetOptKey !== menuSelectKey) setSelectKey(targetOptKey)
  }, [menuSelectKey, item])

  const menuItems: MenuProps['items'] =
    item.children && item.children.length >= 1
      ? item.children.map((child) => ({
          ...child,
          label:
            typeof child.label === 'string' ? (
              <span
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  fontWeight: themeToken.fontWeightStrong,
                  color: 'inherit',
                }}
              >
                {child.label}
              </span>
            ) : (
              child.label
            ),
          onClick: (e) => {
            setSelectKey(child.key)
            // Thực thi onClick
            // Nếu không có children thì click parent
            if (child.children?.length <= 0) {
              menuProps?.onClick?.(e)
              // Hoặc click child đầu tiên
            } else if (child.children?.[0]?.key) {
              menuProps?.onClick?.({
                ...e,
                key:
                  selectNestedFirstKey(
                    child.children[0] as CustomSubMenuType
                  ) || e.key,
              })
            }
          },
          style: { paddingLeft: 18, height: 32 },
          type: 'item',
          children: undefined,
        }))
      : []

  return (
    <div
      {...containerProps}
      style={{
        border: `2px solid ${themeToken['blue-2']}`,
        borderRadius: themeToken.borderRadius,
        marginInline: 4,
        marginBlock: 4,
        paddingBottom: 2,
        overflow: 'hidden',
        ...containerProps?.style,
      }}
    >
      {selectOption ? (
        <>
          {dynamicSelect}
          {showChildren && showChildren.length >= 1 ? (
            <Divider style={{ margin: 0 }} />
          ) : undefined}
          <Menu mode="inline" items={showChildren} {...menuProps} />
        </>
      ) : (
        // Khi không chọn item nào thì sẽ render 1 menu tổng
        <Menu
          mode="inline"
          items={menuItems}
          {...menuProps}
          // Đè onClick do trường hợp này đã xử lý
          onClick={() => {}}
        />
      )}
    </div>
  )
}

export default DynamicMenuRender
